import { Controller } from '@hotwired/stimulus';

// Billables::ClientContactInfo::CLIENT_ID_PLACEHOLDER, in the contact block's URL template.
const CLIENT_ID_PLACEHOLDER = 'CLIENT_ID';

// Billables::ClientField: shows the client card once the selector has a client, and brings
// the selector back on Change, with the client's pill still selected: its X is what removes
// the client, and an outside click without a new choice just brings the card back.
export default class extends Controller {
  static targets = [
    'emptyInput',
    'selector',
    'card',
    'name',
    'email',
    'frame',
    'contact',
    'clientAddress',
    'clientPhone',
    'editLink',
  ];

  #client = null;
  #searching = false;
  #renderedContact = null;

  // A ship-to billable's card shows the client's own contact lines, rendered by the server
  // for one client: the saved one on load, the picked one once its frame loads, and the
  // edited one after the Edit Client modal saves.
  contactTargetConnected(element) {
    this.#renderedContact = {
      clientId: element.dataset.clientId,
      url: element.dataset.url,
      address: this.clientAddressTarget.textContent,
      phone: this.clientPhoneTarget.textContent,
    };
  }

  clientChanged(event) {
    this.#client = event.detail?.clients?.[0] ?? null;
    this.#searching = false;
    this.emptyInputTarget.disabled = this.#client !== null;

    if (this.#client) {
      this.nameTarget.textContent = this.#client.name || this.#client.email || '';
      this.#renderEmail();
      this.#renderClientContact();
    }
    this.element.toggleAttribute('data-selected', this.#client !== null);
  }

  // The selector carries no address or phone: another client's block is loaded from the
  // server, with the lines blank and the edit link hidden until it lands.
  #renderClientContact() {
    if (!this.hasContactTarget) {
      return;
    }
    if (this.#client.id === this.#renderedContact.clientId) {
      this.clientAddressTarget.textContent = this.#renderedContact.address;
      this.clientPhoneTarget.textContent = this.#renderedContact.phone;
      return;
    }
    this.clientAddressTarget.textContent = '';
    this.clientPhoneTarget.textContent = '';
    if (this.hasEditLinkTarget) {
      this.editLinkTarget.classList.add('hidden');
    }
    this.frameTarget.src = this.#renderedContact.url.replace(
      CLIENT_ID_PLACEHOLDER,
      this.#client.id
    );
  }

  // The contact block is re-rendered by the server: for its client the email is the truth
  // (the Edit Client modal may have changed it), for any other the pill's is.
  emailTargetConnected() {
    if (this.#client && this.#client.id !== this.#renderedContact?.clientId) {
      this.#renderEmail();
    }
  }

  change() {
    this.#searching = true;
    this.element.removeAttribute('data-selected');

    const input = this.selectorTarget.querySelector(
      '[data-clients--client-selector-target="input"]'
    );
    // After the click has bubbled: the selector closes its dropdown on any click outside itself.
    setTimeout(() => input.focus());
  }

  #renderEmail() {
    if (this.#client && this.hasEmailTarget) {
      this.emailTarget.textContent = this.#client.email || '';
    }
  }

  clickedOutside(event) {
    if (!this.#searching || !this.#client || this.element.contains(event.target)) {
      return;
    }
    this.#searching = false;
    this.element.setAttribute('data-selected', '');
  }
}
